
import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Edit, Trash2, Eye, MoreHorizontal } from "lucide-react";
import { BlogPost } from "@/types/blogTypes";
import { ScrollArea } from "@/components/ui/scroll-area";

interface BlogPostTableProps {
  posts: BlogPost[];
  onDeleteClick: (id: string) => void;
}

const BlogPostTable = ({ posts, onDeleteClick }: BlogPostTableProps) => {
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <ScrollArea className="w-full overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40%]">Title</TableHead>
            <TableHead className="hidden md:table-cell w-[25%]">Slug</TableHead>
            <TableHead className="w-[10%]">Status</TableHead>
            <TableHead className="hidden md:table-cell w-[10%]">Created</TableHead>
            <TableHead className="hidden md:table-cell w-[10%]">Updated</TableHead>
            <TableHead className="text-right w-[5%]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {posts.length > 0 ? (
            posts.map((post) => (
              <TableRow key={post.id}>
                <TableCell className="font-medium">
                  <div className="max-w-[180px] md:max-w-[250px] xl:max-w-none truncate">
                    {post.title}
                  </div>
                  <div className="text-xs text-gray-500 md:hidden truncate mt-1">
                    {post.slug}
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-gray-500">
                  <div className="max-w-[150px] lg:max-w-[250px] xl:max-w-[300px] truncate">
                    {post.slug}
                  </div>
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    post.published
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}>
                    {post.published ? "Published" : "Draft"}
                  </span>
                </TableCell>
                <TableCell className="hidden md:table-cell">{formatDate(post.created_at)}</TableCell>
                <TableCell className="hidden md:table-cell">{formatDate(post.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/edit-post/${post.id}`)}>
                        <Edit size={14} className="mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => window.open(`/blog/${post.slug}`, '_blank')}>
                        <Eye size={14} className="mr-2" /> View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onDeleteClick(post.id)} className="text-red-600">
                        <Trash2 size={14} className="mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                No blog posts found. Try a different search term or create a new post.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </ScrollArea>
  );
};

export default BlogPostTable;
